import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import type { ModeloLista, PropostaPdfData } from '@shared/types'
import { brl, num } from './format'
import { LOGO_FIRE, LOGO_RSBP, LOGO_WERC } from '../assets/logos'

// ===== Dados FIXOS da Werc (verbatim do PDF de referência — NÃO ALTERAR) =====
const WERC = {
  redator: 'Jyothy Eckert',
  redatorCompleto: 'Jyothy Deva Eckert',
  tel: '+55 41 3022-4848',
  cel: '(41) 9 9954-3938',
  email: 'jy@werc.com.br',
  site: 'www.werc.com.br',
  endereco: 'Rua Flavio Dallegrave',
  cidade: 'Curitiba, PR - 82210-310',
  cnpj: '16.600.254/0001-80',
  ie: '9060307480',
  confirmar: 'jy@werc.com.br / marcos@werc.com.br',
}
const ORANGE: [number, number, number] = [225, 89, 42]
const HEADER_FILL: [number, number, number] = [223, 221, 240]

export interface PropostaOpts {
  euroRate: number
  condicaoPagamento: string
  dataReajusteBase: string
}

const fmtImg = (d: string): 'PNG' | 'JPEG' => (d.includes('image/png') ? 'PNG' : 'JPEG')
const euroBR = (n: number) => `R$ ${n.toFixed(2).replace('.', ',')}`

/** Monta o documento (sem salvar). Exposto para testes/preview. */
export function montarPropostaPDF(data: PropostaPdfData, opts: PropostaOpts): jsPDF {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const W = doc.internal.pageSize.getWidth()
  const M = 12

  capa(doc, data, W, M)
  doc.addPage()
  detalhes(doc, data, opts, W, M)
  doc.addPage()
  produtos(doc, data, W, M)
  termos(doc, opts, W, M)
  return doc
}

export function nomeArquivoProposta(data: PropostaPdfData): string {
  return `${data.clienteRazao} - Werc - Proposta ${data.numeroProposta}`.replace(/[/\\:*?"<>|]/g, '-')
}

export function gerarPropostaPDF(data: PropostaPdfData, opts: PropostaOpts) {
  montarPropostaPDF(data, opts).save(`${nomeArquivoProposta(data)}.pdf`)
}

// ===== Extrato de Estoque =====
// Lista simples dos modelos que TÊM saldo físico (saldo > 0). SEM preço e SEM a
// importação de origem — só o que existe e em que quantidade.
export function montarExtratoEstoquePDF(modelos: ModeloLista[]): jsPDF {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const W = doc.internal.pageSize.getWidth()
  const M = 12

  const itens = modelos.filter((m) => m.saldoAtual > 0)
  const agora = new Date()
  const dataStr = agora.toLocaleDateString('pt-BR')
  const horaStr = agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  const totalUn = itens.reduce((s, m) => s + m.saldoAtual, 0)

  // Cabeçalho (o logo no topo vem do didDrawPage, aplicado a todas as páginas).
  doc.setFillColor(...ORANGE)
  doc.rect(M, 12, W - 2 * M, 4, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(15)
  doc.setTextColor(20)
  doc.text('Extrato de Estoque', M, 26)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9.5)
  doc.setTextColor(90)
  doc.text(`Emitido em ${dataStr} às ${horaStr}`, M, 32)
  doc.text(`${itens.length} modelo(s) em estoque · ${num(totalUn)} unidade(s) no total`, M, 37)

  autoTable(doc, {
    startY: 43,
    head: [['Código', 'Produto', 'Linha', 'Em estoque']],
    body: itens.map((m) => [m.codigoModelo, m.produtoNome, m.linhaNome, num(m.saldoAtual)]),
    margin: { left: M, right: M, top: 16 },
    styles: { fontSize: 9, cellPadding: 2, valign: 'middle', lineColor: [205, 205, 210], lineWidth: 0.1 },
    headStyles: { fillColor: HEADER_FILL, textColor: 30, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [247, 247, 250] },
    columnStyles: {
      0: { cellWidth: 52, fontStyle: 'bold' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 38 },
      3: { cellWidth: 26, halign: 'right' },
    },
    didDrawPage: () => {
      doc.addImage(LOGO_WERC, 'PNG', W - M - 48, 8, 48, 4.1)
      rodape(doc, W)
    },
  })

  return doc
}

export function gerarExtratoEstoquePDF(modelos: ModeloLista[]) {
  const dia = new Date().toISOString().slice(0, 10)
  montarExtratoEstoquePDF(modelos).save(`Extrato de Estoque - ${dia}.pdf`)
}

// ---------- Página 1: capa ----------
function capa(doc: jsPDF, d: PropostaPdfData, W: number, M: number) {
  // Logo (Fire and explosion protection / RSBP / WERC INDUSTRIAL)
  const cx = W / 2
  doc.addImage(LOGO_FIRE, 'PNG', cx - 35, 18, 70, 12.6)
  doc.addImage(LOGO_RSBP, 'PNG', cx - 22, 32, 45, 17.6)
  doc.addImage(LOGO_WERC, 'PNG', cx - 40, 51, 80, 6.9)

  // Caixa do cabeçalho
  let y = 70
  const boxX = M
  const boxW = W - 2 * M
  doc.setDrawColor(60)
  doc.setLineWidth(0.3)
  doc.setFontSize(12)
  doc.setTextColor(20)

  doc.setFont('helvetica', 'normal')
  const leftColX = boxX + 3
  const rightColX = cx + 6
  // Largura disponível p/ o nome da empresa: até a coluna da direita, com folga.
  const leftMaxW = rightColX - leftColX - 3

  // Linha 1: "Empresa: ..." (quebra em várias linhas se for longo) | Nº Proposta.
  // Linha 2: Localidade | Data. A caixa cresce conforme o nº de linhas do nome.
  const empresaLinhas = doc.splitTextToSize(`Empresa: ${d.clienteRazao}`, leftMaxW) as string[]
  const yLocalidade = 6 + (empresaLinhas.length - 1) * 5.5 + 8
  const boxH = yLocalidade + 4

  doc.rect(boxX, y, boxW, boxH)
  empresaLinhas.forEach((ln, i) => doc.text(ln, leftColX, y + 6 + i * 5.5))
  doc.text(`Nº Proposta: ${d.numeroProposta}`, rightColX, y + 6)
  doc.text(`Localidade: ${d.localidade || '—'}`, leftColX, y + yLocalidade)
  doc.text(`Data: ${d.dataProposta}`, rightColX, y + yLocalidade)
  y += boxH + 1

  const bloco = (titulo: string, linhas: string[]) => {
    const h = 6 + linhas.length * 5.2
    doc.rect(boxX, y, boxW, h)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10.5)
    doc.text(titulo, boxX + 3, y + 5)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9.5)
    linhas.forEach((l, i) => doc.text(l, boxX + 3, y + 10 + i * 5.2))
    y += h
  }
  bloco('A/C. ' + (d.contatoNome || '—'), [
    `Depto: ${d.contatoCargo || '—'}`,
    `Tel: ${d.contatoTelefone || '—'}`,
    `E-mail: ${d.contatoEmail || '—'}`,
  ])
  bloco('Werc Industrial', [
    `Redator / Vendas Internas: ${WERC.redator}`,
    `Tel: ${WERC.tel}`,
    `E-mail: ${WERC.email}`,
    'Referência:',
  ])
  rodape(doc, W)
}

// ---------- Página 2: detalhes + notas ----------
function detalhes(doc: jsPDF, d: PropostaPdfData, opts: PropostaOpts, W: number, M: number) {
  doc.setFillColor(...ORANGE)
  doc.rect(M, 12, W - 2 * M, 4, 'F')
  doc.addImage(LOGO_WERC, 'PNG', W - M - 58, 20, 58, 5)
  doc.setFontSize(12)
  doc.setTextColor(20)
  doc.setFont('helvetica', 'normal')
  doc.text(`Assunto: Proposta Comercial - ${d.numeroProposta}`, M, 25)

  let y = 40
  const item = (rotulo: string, linhas: string[]) => {
    doc.setFontSize(11)
    doc.text(rotulo, M, y)
    doc.setFontSize(10)
    linhas.forEach((l, i) => doc.text(l, M + 42, y + i * 5))
    y += Math.max(linhas.length * 5, 6) + 8
  }
  item('Contratante:', [d.contatoNome || d.clienteRazao, d.clienteRazao])
  item('Proponente:', ['Werc Industrial', WERC.endereco, WERC.cidade])
  item('Contato:', [WERC.redatorCompleto, WERC.cel, WERC.email])
  item('Nome do Projeto:', [d.nomeProjeto || d.clienteRazao])
  item('Data de Conclusão:', [d.dataProposta])

  // Notas (fixas + euro inserido)
  doc.setFontSize(11)
  doc.text('Notas:', M, y)
  doc.setFontSize(8.3)
  doc.setTextColor(40)
  // Cada item é um tópico lógico; a quebra visual é automática pela largura,
  // garantindo que cada tópico (inclusive 2 e 6) comece sempre em nova linha.
  const notas = [
    'Notas Especificas;',
    `1 - Faturamento por: WERC INDUSTRIAL LTDA - CNPJ: ${WERC.cnpj}`,
    '2 - No caso do recebimento do Pedido ocorrer em véspera de fins de semana e/ou feriados, o prazo de entrega começará a contar a partir do primeiro dia útil subsequente.',
    'Notas Comerciais;',
    '3 - Preço dos produtos: Posto WERC INDUSTRIAL LTDA / CURITIBA / PR.',
    '4 - Validade da proposta: 30 dias',
    `5 - WERC INDUSTRIAL LTDA - CNPJ: ${WERC.cnpj} - IE: ${WERC.ie} (DADOS PARA FATURAMENTO)`,
    `6 - Para confirmar o pedido: ${WERC.confirmar}`,
    `7 - Email para envio de NFe: ${WERC.email}`,
    '8 - Em caso de cancelamento do pedido, será cobrada indenização de 30% do valor do mesmo.',
    `*OBS Os Preços apresentados nesta Proposta serão reajustados pelo valor do Euro, valor comercial de venda, considerando-se a variação entre a taxa do Euro adotada para cálculo desta Proposta (€1,00 = ${euroBR(opts.euroRate)}) e o valor apurado no dia anterior do efetivo faturamento.`,
  ]
  const notasX = M + 42
  const notasMaxW = W - notasX - M
  let ny = y
  notas.forEach((topico) => {
    const linhas = doc.splitTextToSize(topico, notasMaxW) as string[]
    linhas.forEach((l) => {
      doc.text(l, notasX, ny)
      ny += 4.2
    })
  })
  rodape(doc, W)
}

// ---------- Páginas de produtos ----------
function produtos(doc: jsPDF, d: PropostaPdfData, W: number, M: number) {
  doc.setFontSize(12)
  doc.setTextColor(20)
  doc.setFont('helvetica', 'bold')
  doc.text('PRODUTOS', M, 16)

  const body: Array<Array<{ content: string; styles?: object }>> = []
  d.itens.forEach((it, idx) => {
    const espec =
      (it.especificacao || '').trim() +
      (it.prazoEntrega ? `\nPrazo de entrega: ${it.prazoEntrega}` : '')
    body.push([
      { content: `ITEM ${idx + 1}\n${it.nome}` },
      { content: espec },
      { content: brl(it.precoUnitario), styles: { halign: 'right' } },
      { content: String(it.quantidade), styles: { halign: 'center' } },
      { content: brl(it.valorTotal), styles: { halign: 'right' } },
    ])
    body.push([
      { content: 'ICMS' },
      { content: `${it.icmsPercent}%`, styles: { halign: 'center' } },
      { content: 'Incluso', styles: { halign: 'center' } },
      { content: '', styles: {} },
      { content: 'Incluso', styles: { halign: 'right' } },
    ])
    body.push([
      { content: 'IPI' },
      { content: `${it.ipiPercent}%`, styles: { halign: 'center' } },
      { content: 'Valor total com IPI BRL:', styles: { halign: 'right' } },
      { content: '', styles: {} },
      { content: brl(it.valorComIpi), styles: { halign: 'right', fontStyle: 'bold' } },
    ])
  })

  autoTable(doc, {
    startY: 20,
    head: [['Produto / Código', 'Espec.', 'Preço Unitário', 'Qtd', 'Valor Total']],
    body: body as never,
    margin: { left: M, right: M, top: 18 },
    styles: { fontSize: 8, cellPadding: 1.6, valign: 'top', lineColor: [180, 180, 180], lineWidth: 0.1 },
    headStyles: { fillColor: HEADER_FILL, textColor: 30, fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 36 },
      1: { cellWidth: 86 },
      2: { cellWidth: 26, halign: 'right' },
      3: { cellWidth: 12, halign: 'center' },
      4: { cellWidth: 26, halign: 'right' },
    },
    didParseCell: (h) => {
      // Reserva altura na linha do produto para a imagem.
      if (h.section === 'body' && h.row.index % 3 === 0 && h.column.index === 0) {
        h.cell.styles.minCellHeight = 26
      }
    },
    didDrawCell: (h) => {
      if (h.section === 'body' && h.row.index % 3 === 0 && h.column.index === 0) {
        const it = d.itens[Math.floor(h.row.index / 3)]
        if (it?.imagemDataUrl) {
          try {
            doc.addImage(it.imagemDataUrl, fmtImg(it.imagemDataUrl), h.cell.x + 2, h.cell.y + 9, 30, 15)
          } catch {
            /* imagem inválida: ignora */
          }
        }
      }
    },
    didDrawPage: () => {
      doc.addImage(LOGO_WERC, 'PNG', W - M - 48, 8, 48, 4.1)
      rodape(doc, W)
    },
  })

  // Valor final
  // @ts-expect-error lastAutoTable é adicionado em runtime pelo plugin
  const y = (doc.lastAutoTable?.finalY ?? 40) + 6
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10)
  doc.setTextColor(20)
  doc.text('Valor Final dos produtos:', W - M - 70, y)
  doc.text(brl(d.valorFinal), W - M, y, { align: 'right' })
}

// ---------- Termos + assinatura (após os produtos) ----------
function termos(doc: jsPDF, opts: PropostaOpts, W: number, M: number) {
  // @ts-expect-error runtime
  let y = (doc.lastAutoTable?.finalY ?? 60) + 18
  if (y > 240) {
    doc.addPage()
    doc.addImage(LOGO_WERC, 'PNG', W - M - 48, 8, 48, 4.1)
    rodape(doc, W)
    y = 24
  }
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.setTextColor(30)
  const linhasTermos = [
    '1. Embalagem: s/ ônus',
    '2. Inspeção: isento',
    `3. Condição pgto: ${opts.condicaoPagamento || '—'}`,
    '4. Prazo de entrega: Vide acima',
    '5. Frete: FOB WERC',
    '6. Reajuste de preço: Preço a ser reajustado no faturamento. Não haverá alteração caso a variação',
    `   cambial flutue até 3% para mais ou para menos. Reajustado base dia ${opts.dataReajusteBase} - Euro = ${euroBR(opts.euroRate)}`,
  ]
  linhasTermos.forEach((l, i) => doc.text(l, M, y + i * 5))

  y += linhasTermos.length * 5 + 14
  doc.setFont('helvetica', 'bold')
  doc.text('WERC INDUSTRIAL', M, y)
  doc.setFont('helvetica', 'normal')
  doc.text(WERC.redatorCompleto, M + 55, y)
  doc.setFontSize(8.5)
  doc.text(`cel. ${WERC.cel}`, M + 55, y + 5)
  doc.text(`email - ${WERC.email}`, M + 55, y + 10)
  doc.text(WERC.site, M + 55, y + 15)
}

function rodape(doc: jsPDF, W: number) {
  const h = doc.internal.pageSize.getHeight()
  doc.setDrawColor(120, 160, 210)
  doc.setLineWidth(0.4)
  doc.line(12, h - 12, W - 12, h - 12)
}
